import { analisarPreco, somarCustos } from "../calculator/engine";
import { corFilamentoHex } from "./corFilamento";
import { arredondarHoras } from "./horas";

const EXTRA_ID = "produto-outros-custos";
const numero = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const dinheiro = value => Math.round((value + Number.EPSILON) * 100) / 100;

export function filamentoParaProduto(filamento, peso = "") {
  const custo_g = filamento.custo != null ? numero(filamento.custo) / 1000 : numero(filamento.custo_g);
  return { id:filamento.id, nome:filamento.cor || filamento.nome || "Filamento", material:filamento.material || "", marca:filamento.marca || "", custo_g, peso:String(peso) };
}

function custosDoLote(produto, form, impressoras) {
  const impressora = impressoras.find(i => i.id === form.impressoraId);
  const filamentos = form.filamentos.map(f => ({...f, peso:numero(f.peso), custo_total:numero(f.peso) * numero(f.custo_g)}));
  const adicionais = (produto.custos_adicionais || []).filter(c => c.id !== EXTRA_ID);
  if (numero(form.outrosCustos) > 0) adicionais.push({id:EXTRA_ID, nome:"Outros custos", tipo:"fixo", valor:numero(form.outrosCustos)});
  const custos = somarCustos({
    filamentos, materiaisExtras:produto.materiais_usados || [], custosAdicionais:adicionais,
    maoDeObra:{horas:numero(produto.mao_obra_hrs), valorHora:numero(produto.mao_obra_valor_hr)},
    energia:{modo:"simples", horas:numero(form.horas), valorHora:numero(impressora?.consumo_hr)},
    desgaste:{modo:"simples", horas:numero(form.horas), valorHora:numero(impressora?.desgaste_hr)},
  });
  return {...custos, filamentos, adicionais, impressora};
}

export function prepararProducaoProduto(produto, impressoras = []) {
  const unidades = Math.max(1, numero(produto.unidades) || 1);
  const form = {
    alterada:false, unidades:String(unidades), horas:String(numero(produto.horas_impressao)),
    impressoraId:produto.impressora_id || "", outrosCustos:"0",
    // O cadastro guarda filamento por peça; a edição trabalha com o lote inteiro.
    filamentos:(produto.filamentos_usados || []).map((f, index) => ({...f, rowId:`salvo-${index}`, peso:String(numero(f.peso) * unidades), custo_g:f.custo_g != null ? numero(f.custo_g) : numero(f.peso) > 0 ? numero(f.custo_total) / numero(f.peso) : 0})),
  };
  const adicional = (produto.custos_adicionais || []).find(c => c.id === EXTRA_ID);
  const custoConhecido = custosDoLote(produto, form, impressoras).custoTotal;
  // Mantém custos antigos sem detalhamento (ex.: custo importado/manual).
  form.outrosCustos = String(adicional ? numero(adicional.valor) : dinheiro(Math.max(0, numero(produto.custo_total) * unidades - custoConhecido)));
  // Só DEPOIS de apurar os custos com o tempo exato: o campo mostra o tempo
  // arredondado (produtos antigos guardam algo como 1,1085457356770834), mas
  // isso não pode mexer no "outros custos" derivado acima.
  form.horas = String(arredondarHoras(numero(produto.horas_impressao)));
  return form;
}

export function analisarProdutoEditado(produto, form, preco, impressoras = []) {
  const calculo = custosDoLote(produto, form, impressoras);
  const unidades = Math.max(1, numero(form.unidades));
  const custo = !form.alterada && produto.custo_total != null ? numero(produto.custo_total) : calculo.custoTotal / unidades;
  const custoUnitario = dinheiro(custo);
  const resultado = analisarPreco({precoFinal:dinheiro(numero(preco)), custoTotal:custoUnitario, taxas:produto.taxas_usadas || []});
  const detalhamento = !form.alterada && produto.custo_total != null && Math.abs(calculo.custoTotal / unidades - custoUnitario) > .01
    ? [{label:"Custo cadastrado", valor:custoUnitario}]
    : calculo.detalhamento.map(linha => ({...linha, valor:linha.valor / unidades}));
  return {...calculo, ...resultado, unidades, custoLote:custoUnitario * unidades, detalhamento};
}

export function erroProducaoProduto(form, preco) {
  if (String(preco).trim() !== "" && (!Number.isFinite(Number(preco)) || Number(preco) < 0 || !Number.isSafeInteger(Math.round(Number(preco)*100)))) return "Informe um preço de venda válido.";
  if (!Number.isSafeInteger(Number(form.unidades)) || Number(form.unidades) < 1) return "Informe pelo menos uma peça por impressão.";
  if (form.horas === "" || !Number.isFinite(Number(form.horas)) || Number(form.horas) < 0) return "Informe um tempo de impressão válido.";
  if (form.outrosCustos === "" || !Number.isFinite(Number(form.outrosCustos)) || Number(form.outrosCustos) < 0) return "Informe um valor válido para os outros custos.";
  if (form.filamentos.some(f => !f.id || !Number.isFinite(Number(f.peso)) || Number(f.peso) <= 0)) return "Selecione o filamento e informe a quantidade em gramas.";
  return "";
}

export function patchProducaoProduto(produto, form, preco, impressoras = [], coresCustom = []) {
  const erro = erroProducaoProduto(form, preco);
  if (erro) throw new Error(erro);
  const resultado = analisarProdutoEditado(produto, form, preco, impressoras);
  if (!Number.isFinite(resultado.custoTotal) || !Number.isFinite(resultado.custoLote)) throw new Error("Revise os valores de produção.");
  const patch = {
    preco:String(preco).trim() === "" ? null : dinheiro(numero(preco)),
    custo_total:resultado.custoTotal,
    margem:resultado.custoTotal > 0 ? (resultado.precoFinal - resultado.custoTotal) / resultado.custoTotal * 100 : 0,
    margem_pct:resultado.margemReal, markup:resultado.markup,
  };
  if (!form.alterada) return patch;
  const filamentos = resultado.filamentos.map(({rowId, ...f}) => ({...f, peso:f.peso / resultado.unidades, custo_total:f.custo_total / resultado.unidades}));
  return {
    ...patch, unidades:resultado.unidades, horas_impressao:numero(form.horas),
    impressora_id:form.impressoraId || null,
    impressora_nome:resultado.impressora?.nome || (form.impressoraId === produto.impressora_id ? produto.impressora_nome || null : null),
    filamentos_usados:filamentos, custos_adicionais:resultado.adicionais,
    filamento_id:filamentos[0]?.id || null, filamento_nome:filamentos[0]?.nome || null,
    peso:filamentos.reduce((s,f)=>s+f.peso,0),
    cores:filamentos.map(f=>({nome:f.nome, material:f.material || "", hex:corFilamentoHex(f.nome, coresCustom)})),
  };
}